import React from 'react';
import {
  requestPermissionAndGetToken,
  saveFcmToken,
  updateFcmPrefs,
  disableFcmToken,
} from '../firebase';

const FCM_KEY_STORAGE = 'azan:fcmKey';

export default function SettingsModal({
  isOpen,
  onClose,
  prefs,
  setPrefs,
  playSound,
  speak,
}) {
  if (!isOpen) return null;

  const getKey = () => {
    try {
      return localStorage.getItem(FCM_KEY_STORAGE);
    } catch {
      return null;
    }
  };

  const setKey = (key) => {
    try {
      if (key) localStorage.setItem(FCM_KEY_STORAGE, key);
    } catch {}
  };

  const clearKey = () => {
    try {
      localStorage.removeItem(FCM_KEY_STORAGE);
    } catch {}
  };

  // Inline Close Icon
  const CloseIcon = () => (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      fill='none'
      viewBox='0 0 24 24'
      strokeWidth={2}
      stroke='currentColor'
      className='w-6 h-6'
    >
      <path
        strokeLinecap='round'
        strokeLinejoin='round'
        d='M6 18L18 6M6 6l12 12'
      />
    </svg>
  );

  // 🔔 Enable / Disable Reminders (FCM-based)
  const handleReminderToggle = async (e) => {
    const enabled = e.target.checked;
    setPrefs((p) => ({ ...p, enabled }));

    if (enabled) {
      try {
        const token = await requestPermissionAndGetToken();
        if (!token) {
          alert(
            '❌ Unable to enable notifications. Permission or token failed.',
          );
          setPrefs((p) => ({ ...p, enabled: false }));
          return;
        }

        // Save token + current prefs to DB (minutesBefore)
        const key = await saveFcmToken(null, token, {
          minutesBefore: prefs.minutesBefore,
        });
        setKey(key);

        if ('Notification' in window && Notification.permission === 'granted') {
          new Notification('✅ Notifications enabled', {
            body: 'Prayer reminders will be sent before each prayer time.',
          });
        }

        console.log('✅ Notifications enabled & FCM token saved:', key);
      } catch (err) {
        console.error('⚠️ Error enabling notifications:', err);
        alert('❌ Something went wrong while enabling notifications.');
        setPrefs((p) => ({ ...p, enabled: false }));
      }
    } else {
      // Disable — remove token mapping in DB
      const key = getKey();
      if (key) {
        try {
          await disableFcmToken(key);
          console.log('🔕 Notifications disabled for key:', key);
        } catch (err) {
          console.warn('⚠️ Failed to disable FCM token in DB:', err);
        }
      }
      clearKey();
    }
  };

  // ⏰ Update minutesBefore both locally & in DB (if FCM key exists)
  const handleMinutesBeforeChange = async (e) => {
    const newValue = Number(e.target.value);
    console.log(
      `⏰ Reminder time selected: ${newValue} minutes before prayer.`,
    );

    setPrefs((p) => ({ ...p, minutesBefore: newValue }));

    const key = getKey();
    if (key) {
      try {
        await updateFcmPrefs(key, { minutesBefore: newValue });
        console.log('✅ Updated FCM reminder minutes for key:', key);
      } catch (err) {
        console.warn('⚠️ Failed to update FCM prefs:', err);
      }
    }
  };

  return (
    <div className='fixed inset-0 bg-black/40 flex items-center justify-center z-50'>
      <div className='bg-white w-full max-w-md rounded-2xl shadow-2xl p-6 relative animate-fade-in'>
        {/* Close Button */}
        <button
          onClick={onClose}
          className='absolute top-3 right-3 text-gray-400 hover:text-gray-600'
        >
          <CloseIcon />
        </button>

        {/* Header */}
        <h2 className='text-2xl font-bold text-indigo-700 mb-4'>
          Notification Settings
        </h2>
        <p className='text-sm text-gray-500 mb-6'>
          Customize your prayer reminders, sounds, and voice notifications.
        </p>

        {/* Enable Toggle (keep old UI) */}
        <div className='flex justify-between items-center mb-4'>
          <span className='font-medium text-gray-700'>Enable Reminders</span>
          <label className='inline-flex items-center cursor-pointer'>
            <input
              type='checkbox'
              className='sr-only peer'
              checked={prefs.enabled}
              onChange={handleReminderToggle}
            />
            <div className='w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-indigo-600 relative transition'>
              <div
                className={`absolute top-0.5 left-0.5 h-5 w-5 bg-white rounded-full transition ${
                  prefs.enabled ? 'translate-x-5' : ''
                }`}
              />
            </div>
          </label>
        </div>

        {/* Reminder Time Dropdown */}
        <div className='mb-4'>
          <label className='block text-sm font-medium text-gray-600 mb-1'>
            Reminder Time
          </label>
          <select
            className='w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-400'
            value={prefs.minutesBefore}
            onChange={handleMinutesBeforeChange}
          >
            <option value={30}>30 minutes before</option>
            <option value={45}>45 minutes before</option>
            <option value={60}>60 minutes before</option>
          </select>
        </div>

        {/* Sound & Voice Toggles */}
        <div className='flex flex-col sm:flex-row gap-3 mb-5'>
          {[
            { label: 'Sound', key: 'sound' },
            { label: 'Voice', key: 'voice' },
          ].map(({ label, key }) => (
            <label
              key={key}
              className='inline-flex items-center cursor-pointer'
            >
              <input
                type='checkbox'
                className='sr-only peer'
                checked={prefs[key]}
                onChange={(e) =>
                  setPrefs((p) => ({ ...p, [key]: e.target.checked }))
                }
              />
              <div className='w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-indigo-600 relative transition'>
                <div
                  className={`absolute top-0.5 left-0.5 h-5 w-5 bg-white rounded-full transition ${
                    prefs[key] ? 'translate-x-5' : ''
                  }`}
                />
              </div>
              <span className='ml-3 text-sm text-gray-700'>{label}</span>
            </label>
          ))}
        </div>

        {/* Test Buttons */}
        <div className='flex gap-3 justify-end flex-wrap'>
          <button
            onClick={playSound}
            className='px-3 py-2 text-sm bg-indigo-50 text-indigo-700 rounded-lg border border-indigo-200 hover:bg-indigo-100'
          >
            Test Sound
          </button>

          <button
            onClick={() => {
              if (prefs.voice && speak) {
                speak('This is a test of the voice reminder.');
              } else {
                alert('Voice Reminder is disabled in the settings.');
              }
            }}
            className='px-3 py-2 text-sm bg-purple-50 text-purple-700 rounded-lg border border-purple-200 hover:bg-purple-100'
          >
            Test Voice
          </button>

          <button
            onClick={() => {
              if ('Notification' in window) {
                if (Notification.permission === 'default') {
                  Notification.requestPermission().then((perm) => {
                    if (perm === 'granted')
                      new Notification('✅ Notifications enabled');
                  });
                } else if (Notification.permission === 'granted') {
                  new Notification('🔔 Test notification', {
                    body: 'Looks good!',
                  });
                } else {
                  alert(
                    'Notifications are blocked in browser settings. Please enable them manually.',
                  );
                }
              } else {
                alert('Notifications are not supported in this browser.');
              }
            }}
            className='px-3 py-2 text-sm bg-gray-50 text-gray-700 rounded-lg border hover:bg-gray-100'
          >
            Test Notification
          </button>
        </div>
      </div>
    </div>
  );
}
